import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  setSidebarTab,
  setSidebarCat,
  toggleFavorite,
  setActiveInstrument,
  openModal,
  setLotSize,
} from "../../store/uiSlice";
import { fmtP, fmtUSD, getChangeClass } from "../../utils/formatters";
import {
  INSTRUMENTS,
  CAT_ICONS,
  CAT_ORDER,
  getLotSize,
  LOT_LABELS,
} from "../../utils/constants";
import { getMarketStatus } from "../../utils/marketHours";
import api from "../../services/api";

const CAT_LABELS = {
  all: "All Categories",
  forex: "Forex",
  crypto: "Crypto",
  metal: "Metals",
  stock: "Stocks",
  index: "Indices",
  commodity: "Commodities",
};

const DISPLAY_IDS = {
  IXIC: "NASDAQ",
  DJI: "Dow Jones",
  N225: "JN225",
  RY: "USDTRY",
};

const getDisplayId = (inst) => {
  const baseId = inst.id.replace("USDT", "");
  return DISPLAY_IDS[baseId] || DISPLAY_IDS[inst.id] || baseId;
};

// Currency to flag mapping
const CURRENCY_FLAGS = {
  USD: "us", EUR: "eu", GBP: "gb", JPY: "jp", CHF: "ch",
  AUD: "au", CAD: "ca", NZD: "nz", CNY: "cn", CNH: "cn",
  HKD: "hk", SGD: "sg", SEK: "se", NOK: "no", DKK: "dk",
  PLN: "pl", TRY: "tr", RUB: "ru", ZAR: "za", MXN: "mx",
  INR: "in", BRL: "br", KRW: "kr", THB: "th", IDR: "id",
  MYR: "my", PHP: "ph", VND: "vn", ILS: "il", CZK: "cz",
  HUF: "hu", RON: "ro", BGN: "bg", HRK: "hr", ISK: "is",
  CLP: "cl", COP: "co", PEN: "pe", ARS: "ar", EGP: "eg",
  NGN: "ng", KES: "ke", MAD: "ma", TND: "tn", GHS: "gh",
  QAR: "qa", AED: "ae", SAR: "sa", KWD: "kw", BHD: "bh",
  OMR: "om", JOD: "jo", LBP: "lb", IQD: "iq", IRR: "ir",
  AFN: "af", PKR: "pk", BDT: "bd", LKR: "lk", MMK: "mm",
  KHR: "kh", LAK: "la", TWD: "tw", UAH: "ua", KZT: "kz",
  UZS: "uz", AZN: "az", GEL: "ge", AMD: "am", BYN: "by",
  MDL: "md", MKD: "mk", ALL: "al", RSD: "rs", BAM: "ba",
  MNT: "mn", BND: "bn", BWP: "bw", CVE: "cv", ETB: "et",
  FJD: "fj", GMD: "gm", GNF: "gn", GTQ: "gt", HNL: "hn",
  HTG: "ht", JMD: "jm", KGS: "kg", KMF: "km", LSL: "ls",
  LYD: "ly", MGA: "mg", MOP: "mo", MUR: "mu", MWK: "mw",
  MZN: "mz", NAD: "na", NPR: "np", PAB: "pa", PGK: "pg",
  PYG: "py", RWF: "rw", SCR: "sc", SLL: "sl", SOS: "so",
  SRD: "sr", SZL: "sz", TJS: "tj", TMT: "tm", TOP: "to",
  TTD: "tt", UGX: "ug", UYU: "uy", VES: "ve", VUV: "vu",
  WST: "ws", XAF: "cm", XCD: "ag", XOF: "sn", XPF: "pf",
  YER: "ye", ZMW: "zm", ZWL: "zw",
};

// Stock to country mapping - CORRECTED
const STOCK_COUNTRIES = {
  // US Stocks
  AAPL: "us", MSFT: "us", GOOGL: "us", GOOG: "us", AMZN: "us",
  META: "us", FB: "us", NVDA: "us", NFLX: "us", AMD: "us",
  INTC: "us", CRM: "us", ADBE: "us", ORCL: "us", IBM: "us",
  CSCO: "us", QCOM: "us", TXN: "us", AVGO: "us", MU: "us",
  AMAT: "us", LRCX: "us", KLAC: "us", ADI: "us", MCHP: "us",
  NXPI: "us", MRVL: "us", ON: "us", TSLA: "us", PYPL: "us",
  SQ: "us", BLOCK: "us", TWTR: "us", SNAP: "us", UBER: "us",
  LYFT: "us", ZM: "us", PLTR: "us", COIN: "us", RBLX: "us",
  DKNG: "us", PTON: "us", ABNB: "us", CRWD: "us", SNOW: "us",
  DASH: "us", DOCU: "us", OKTA: "us", ZS: "us", NET: "us",
  MDB: "us", DDOG: "us", HUBS: "us", TEAM: "us", WDAY: "us",
  ADSK: "us", ANSS: "us", CDNS: "us", SNPS: "us", FTNT: "us",
  PANW: "us", NOW: "us", VEEV: "us", JPM: "us", BAC: "us",
  WFC: "us", C: "us", GS: "us", MS: "us", V: "us",
  MA: "us", AXP: "us", DFS: "us", COF: "us", USB: "us",
  PNC: "us", TFC: "us", BK: "us", STT: "us", SCHW: "us",
  BLK: "us", WMT: "us", TGT: "us", COST: "us", HD: "us",
  LOW: "us", MCD: "us", SBUX: "us", NKE: "us", DIS: "us",
  KO: "us", PEP: "us", PG: "us", JNJ: "us", MRK: "us",
  PFE: "us", LLY: "us", ABBV: "us", BMY: "us", AMGN: "us",
  GILD: "us", CVS: "us", WBA: "us", UNH: "us", CI: "us",
  HUM: "us", BA: "us", CAT: "us", GE: "us", MMM: "us",
  HON: "us", UPS: "us", FDX: "us", LMT: "us", RTX: "us",
  NOC: "us", GD: "us", DE: "us", EMR: "us", ETN: "us",
  ROK: "us", XOM: "us", CVX: "us", COP: "us", EOG: "us",
  SLB: "us", HAL: "us", KMI: "us", WMB: "us", OKE: "us",
  PSX: "us", T: "us", VZ: "us", TMUS: "us", CMCSA: "us",
  CHTR: "us", F: "us", GM: "us", RIVN: "us", LCID: "us",
  
  // Chinese Stocks
  BABA: "cn", BIDU: "cn", JD: "cn", PDD: "cn", NIO: "cn",
  XPEV: "cn", LI: "cn", TME: "cn", NTES: "cn", EDU: "cn",
  FUTU: "cn", BILI: "cn", IQ: "cn", WB: "cn", ZTO: "cn",
  TAL: "cn", YUMC: "cn", TCOM: "cn", VIPS: "cn", DADA: "cn",
  BZUN: "cn", GDS: "cn", KC: "cn", LX: "cn", MOMO: "cn",
  QFIN: "cn", SOHU: "cn", YY: "cn", ZLAB: "cn",
  
  // European Stocks
  SAP: "de", VOW3: "de", BMW: "de", MBG: "de", BAYN: "de",
  DTE: "de", ALV: "de", BAS: "de", SIE: "de", IFX: "de",
  DBK: "de", HEN3: "de", RWE: "de", EON: "de", DPW: "de",
  AIR: "fr", BNP: "fr", DG: "fr", KER: "fr", MC: "fr",
  OR: "fr", TTE: "fr", VIE: "fr", NESN: "ch", NOVN: "ch",
  ROG: "ch", UBSG: "ch", ASML: "nl", INGA: "nl", PHIA: "nl",
  UNA: "nl", SAN: "es", BBVA: "es", TEF: "es", ITX: "es",
  IBE: "es", ENI: "it", ENEL: "it", ISP: "it", UCG: "it",
  
  // UK Stocks
  HSBC: "gb", BP: "gb", GSK: "gb", AZN: "gb", ULVR: "gb",
  RIO: "gb", VOD: "gb", LLOY: "gb", BARC: "gb", BATS: "gb",
  DGE: "gb", PRU: "gb", AV: "gb", STAN: "gb", TSCO: "gb",
  SBRY: "gb", NG: "gb", SSE: "gb", BT: "gb",
  
  // Japanese Stocks
  TM: "jp", SONY: "jp", MUFG: "jp", SMFG: "jp", NTT: "jp",
  HMC: "jp", MFG: "jp", CAJ: "jp", SNE: "jp", TAK: "jp",
  
  // Canadian Stocks
  RY: "ca", TD: "ca", BNS: "ca", BMO: "ca", CM: "ca",
  ENB: "ca", CNQ: "ca", TRP: "ca", SU: "ca", CP: "ca",
  CNR: "ca", BCE: "ca", MFC: "ca", SLF: "ca",
  
  // Australian Stocks
  BHP: "au", CSL: "au", WBC: "au", NAB: "au", ANZ: "au",
  CBA: "au", MQG: "au", WES: "au", TLS: "au", WOW: "au",
  
  // Brazilian Stocks
  VALE: "br", PBR: "br", ITUB: "br", BBD: "br", ABEV: "br",
  
  // Indian Stocks - CORRECTED
  INFY: "in", WIT: "in", HDB: "in", IBN: "in", TTM: "in",
  TATAMOTORS: "in", TATAMTRDVR: "in", TCS: "in", RELIANCE: "in",
  HINDUNILVR: "in", ADANIENT: "in", ICICIBANK: "in", SBIN: "in",
  BHARTIARTL: "in", MARUTI: "in", HDFCBANK: "in", HDFC: "in",
  KOTAKBANK: "in", AXISBANK: "in", LT: "in", ITC: "in",
  WIPRO: "in", HCLTECH: "in", TECHM: "in", SUNPHARMA: "in",
  DRREDDY: "in", CIPLA: "in", BAJFINANCE: "in", BAJAJFINSV: "in",
  ASIANPAINT: "in", TITAN: "in", ULTRACEMCO: "in", JSWSTEEL: "in",
  TATASTEEL: "in", HINDALCO: "in", VEDL: "in", ONGC: "in",
  NTPC: "in", POWERGRID: "in", IOC: "in", BPCL: "in",
  GAIL: "in", COALINDIA: "in", HEROMOTOCO: "in", EICHERMOT: "in",
  MANDM: "in", GRASIM: "in", SHREECEM: "in", DIVISLAB: "in",
  PIDILITIND: "in", BRITANNIA: "in", NESTLEIND: "in", DABUR: "in",
  GODREJCP: "in", MARICO: "in", UPL: "in",
  
  // Other International
  ERIC: "se", SPOT: "se", NOK: "fi", AMS: "at", TSM: "tw",
  STM: "fr", SHOP: "ca",
};

// Crypto icons mapping
const CRYPTO_ICONS = {
  BTC: "https://cryptologos.cc/logos/bitcoin-btc-logo.svg",
  ETH: "https://cryptologos.cc/logos/ethereum-eth-logo.svg",
  BNB: "https://cryptologos.cc/logos/bnb-bnb-logo.svg",
  XRP: "https://cryptologos.cc/logos/xrp-xrp-logo.svg",
  ADA: "https://cryptologos.cc/logos/cardano-ada-logo.svg",
  SOL: "https://cryptologos.cc/logos/solana-sol-logo.svg",
  DOGE: "https://cryptologos.cc/logos/dogecoin-doge-logo.svg",
  DOT: "https://cryptologos.cc/logos/polkadot-new-dot-logo.svg",
  LTC: "https://cryptologos.cc/logos/litecoin-ltc-logo.svg",
  LINK: "https://cryptologos.cc/logos/chainlink-link-logo.svg",
  MATIC: "https://cryptologos.cc/logos/polygon-matic-logo.svg",
  AVAX: "https://cryptologos.cc/logos/avalanche-avax-logo.svg",
  UNI: "https://cryptologos.cc/logos/uniswap-uni-logo.svg",
  ATOM: "https://cryptologos.cc/logos/cosmos-atom-logo.svg",
  ETC: "https://cryptologos.cc/logos/ethereum-classic-etc-logo.svg",
  FIL: "https://cryptologos.cc/logos/filecoin-fil-logo.svg",
  APT: "https://cryptologos.cc/logos/aptos-apt-logo.svg",
  ARB: "https://cryptologos.cc/logos/arbitrum-arb-logo.svg",
  OP: "https://cryptologos.cc/logos/optimism-op-logo.svg",
  NEAR: "https://cryptologos.cc/logos/near-protocol-near-logo.svg",
  ALGO: "https://cryptologos.cc/logos/algorand-algo-logo.svg",
  VET: "https://cryptologos.cc/logos/vechain-vet-logo.svg",
  ICP: "https://cryptologos.cc/logos/internet-computer-icp-logo.svg",
  SAND: "https://cryptologos.cc/logos/the-sandbox-sand-logo.svg",
  MANA: "https://cryptologos.cc/logos/decentraland-mana-logo.svg",
  AXS: "https://cryptologos.cc/logos/axie-infinity-axs-logo.svg",
  GALA: "https://cryptologos.cc/logos/gala-gala-logo.svg",
  ENS: "https://cryptologos.cc/logos/ethereum-name-service-ens-logo.svg",
  CRV: "https://cryptologos.cc/logos/curve-dao-token-crv-logo.svg",
  AAVE: "https://cryptologos.cc/logos/aave-aave-logo.svg",
  COMP: "https://cryptologos.cc/logos/compound-comp-logo.svg",
  MKR: "https://cryptologos.cc/logos/maker-mkr-logo.svg",
  SNX: "https://cryptologos.cc/logos/synthetix-snx-logo.svg",
  YFI: "https://cryptologos.cc/logos/yearn-finance-yfi-logo.svg",
  SUSHI: "https://cryptologos.cc/logos/sushiswap-sushi-logo.svg",
  ONE: "https://cryptologos.cc/logos/harmony-one-logo.svg",
  CELO: "https://cryptologos.cc/logos/celo-celo-logo.svg",
  ZEC: "https://cryptologos.cc/logos/zcash-zec-logo.svg",
  XMR: "https://cryptologos.cc/logos/monero-xmr-logo.svg",
  DASH: "https://cryptologos.cc/logos/dash-dash-logo.svg",
  EOS: "https://cryptologos.cc/logos/eos-eos-logo.svg",
  TRX: "https://cryptologos.cc/logos/tron-trx-logo.svg",
  XLM: "https://cryptologos.cc/logos/stellar-xlm-logo.svg",
  HBAR: "https://cryptologos.cc/logos/hedera-hbar-logo.svg",
  QNT: "https://cryptologos.cc/logos/quant-qnt-logo.svg",
  GRT: "https://cryptologos.cc/logos/the-graph-grt-logo.svg",
  CHZ: "https://cryptologos.cc/logos/chiliz-chz-logo.svg",
  ENJ: "https://cryptologos.cc/logos/enjin-coin-enj-logo.svg",
  BAT: "https://cryptologos.cc/logos/basic-attention-token-bat-logo.svg",
  ZRX: "https://cryptologos.cc/logos/0x-zrx-logo.svg",
  KNC: "https://cryptologos.cc/logos/kyber-network-knc-logo.svg",
  REN: "https://cryptologos.cc/logos/ren-ren-logo.svg",
  STORJ: "https://cryptologos.cc/logos/storj-storj-logo.svg",
  ANKR: "https://cryptologos.cc/logos/ankr-ankr-logo.svg",
  CTSI: "https://cryptologos.cc/logos/cartesi-ctsi-logo.svg",
  BAND: "https://cryptologos.cc/logos/band-protocol-band-logo.svg",
  MASK: "https://cryptologos.cc/logos/mask-network-mask-logo.svg",
  INJ: "https://cryptologos.cc/logos/injective-inj-logo.svg",
  KAVA: "https://cryptologos.cc/logos/kava-kava-logo.svg",
  ICX: "https://cryptologos.cc/logos/icon-icx-logo.svg",
  ONT: "https://cryptologos.cc/logos/ontology-ont-logo.svg",
  ZIL: "https://cryptologos.cc/logos/zilliqa-zil-logo.svg",
  THETA: "https://cryptologos.cc/logos/theta-theta-logo.svg",
  HNT: "https://cryptologos.cc/logos/helium-hnt-logo.svg",
  FLOW: "https://cryptologos.cc/logos/flow-flow-logo.svg",
  EGLD: "https://cryptologos.cc/logos/elrond-egld-logo.svg",
  XTZ: "https://cryptologos.cc/logos/tezos-xtz-logo.svg",
  NEO: "https://cryptologos.cc/logos/neo-neo-logo.svg",
  WAVES: "https://cryptologos.cc/logos/waves-waves-logo.svg",
  KSM: "https://cryptologos.cc/logos/kusama-ksm-logo.svg",
  RUNE: "https://cryptologos.cc/logos/thorchain-rune-logo.svg",
  USDT: "https://cryptologos.cc/logos/tether-usdt-logo.svg",
  USDC: "https://cryptologos.cc/logos/usd-coin-usdc-logo.svg",
  BUSD: "https://cryptologos.cc/logos/binance-usd-busd-logo.svg",
  DAI: "https://cryptologos.cc/logos/dai-dai-logo.svg",
};

// Function to get forex flags
const getForexFlags = (instId) => {
  let cleanId = instId.toUpperCase().replace("USDT", "");
  
  if (cleanId === "RY") {
    return ["us", "tr"];
  }
  
  if (cleanId.length === 6) {
    const first = cleanId.substring(0, 3);
    const second = cleanId.substring(3, 6);
    
    if (CURRENCY_FLAGS[first] && CURRENCY_FLAGS[second]) {
      return [CURRENCY_FLAGS[first], CURRENCY_FLAGS[second]];
    }
  }
  
  const matches = cleanId.match(/[A-Z]{3}/g);
  if (matches && matches.length >= 2) {
    const first = matches[0];
    const second = matches[1];
    
    if (CURRENCY_FLAGS[first] && CURRENCY_FLAGS[second]) {
      return [CURRENCY_FLAGS[first], CURRENCY_FLAGS[second]];
    }
  }
  
  return null;
};

// Function to get crypto icon
const getCryptoIcon = (instId) => {
  const cleanId = instId.toUpperCase().replace("USDT", "").replace("USD", "");
  return CRYPTO_ICONS[cleanId] || CRYPTO_ICONS[instId] || null;
};

// Function to get stock country flag
const getStockFlag = (instId) => {
  const cleanId = instId.toUpperCase().replace("USDT", "").replace("USD", "");
  
  if (STOCK_COUNTRIES[cleanId]) {
    return STOCK_COUNTRIES[cleanId];
  }
  
  if (STOCK_COUNTRIES[instId]) {
    return STOCK_COUNTRIES[instId];
  }
  
  // Default to US flag for unknown stocks
  return "us";
};

// Forex flags component
const ForexFlags = ({ instId }) => {
  const flags = getForexFlags(instId);
  if (!flags) return null;
  
  return (
    <div style={{ display: "flex", gap: "1px", alignItems: "center", flexShrink: 0 }}>
      <img
        src={`https://flagcdn.com/w20/${flags[0]}.png`}
        srcSet={`https://flagcdn.com/w40/${flags[0]}.png 2x`}
        alt={flags[0]}
        width={15}
        height={11}
        style={{ objectFit: "cover", borderRadius: "1px" }}
        onError={(e) => { e.target.style.display = "none"; }}
      />
      <img
        src={`https://flagcdn.com/w20/${flags[1]}.png`}
        srcSet={`https://flagcdn.com/w40/${flags[1]}.png 2x`}
        alt={flags[1]}
        width={15}
        height={11}
        style={{ objectFit: "cover", borderRadius: "1px" }}
        onError={(e) => { e.target.style.display = "none"; }}
      />
    </div>
  );
};

// Crypto icon component
const CryptoIcon = ({ instId }) => {
  const iconUrl = getCryptoIcon(instId);
  if (!iconUrl) return null;
  
  return (
    <img
      src={iconUrl}
      alt={instId}
      width={15}
      height={15}
      style={{ borderRadius: "50%", flexShrink: 0 }}
      onError={(e) => { e.target.style.display = "none"; }}
    />
  );
};

// Stock flag component
const StockFlag = ({ instId }) => {
  const countryCode = getStockFlag(instId);
  if (!countryCode) return null;
  
  return (
    <img
      src={`https://flagcdn.com/w20/${countryCode}.png`}
      srcSet={`https://flagcdn.com/w40/${countryCode}.png 2x`}
      alt={countryCode}
      width={15}
      height={11}
      style={{ objectFit: "cover", borderRadius: "1px", flexShrink: 0 }}
      onError={(e) => { e.target.style.display = "none"; }}
    />
  );
};

// Main Instrument Icon component
const InstrumentIcon = ({ inst }) => {
  if (inst.cat === "forex") {
    return <ForexFlags instId={inst.id} />;
  }
  
  if (inst.cat === "crypto") {
    return <CryptoIcon instId={inst.id} />;
  }
  
  if (inst.cat === "stock") {
    return <StockFlag instId={inst.id} />;
  }
  
  return null;
};

const MIN_WIDTH = 120;
const COLLAPSE_WIDTH = 60;

const Sidebar = () => {
  const dispatch = useDispatch();
  const { sidebarTab, sidebarCat, favorites, activeInstrument, lotSize } =
    useSelector((state) => state.ui);
  const { marketData, portfolio, balance } = useSelector(
    (state) => state.trade,
  );
  const { user } = useSelector((state) => state.auth);

  const [searchQuery, setSearchQuery] = useState("");
  const [openCategories, setOpenCategories] = useState({
    forex: true,
    crypto: true,
    metal: true,
    stock: true,
    index: true,
    commodity: true,
  });
  const [priceFlash, setPriceFlash] = useState({});
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem("sidebarWidth");
    const thirtyPercent = window.innerWidth * 0.3;

    if (saved) {
      const parsed = parseFloat(saved);
      if (
        parsed >= window.innerWidth * 0.2 &&
        parsed <= window.innerWidth * 0.4
      ) {
        return parsed;
      }
    }

    return thirtyPercent;
  });
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved === "true";
  });
  const [expandedInstrument, setExpandedInstrument] = useState(null);
  const [manualLotInput, setManualLotInput] = useState(String(lotSize || 0.01));
  const [bottomTab, setBottomTab] = useState("info");
  const [splitRatio, setSplitRatio] = useState(() => {
    const saved = localStorage.getItem("sidebarSplitRatio");
    return saved ? parseFloat(saved) : 0.8;
  });
  const [totalCredit, setTotalCredit] = useState(0);
  const [bonusBalance, setBonusBalance] = useState(0);

  const prevPricesRef = useRef({});
  const activeRowRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const sidebarRef = useRef(null);
  const isDraggingSidebar = useRef(false);
  const isDraggingSplit = useRef(false);
  const dragStartX = useRef(0);
  const dragStartWidth = useRef(0);
  const dragStartY = useRef(0);
  const dragStartRatio = useRef(0);
  const lastWidthRef = useRef(240);

  useEffect(() => {
    const shouldForceThirtyPercent =
      !localStorage.getItem("sidebarCustomWidth");
    if (shouldForceThirtyPercent) {
      const thirtyPercentWidth = window.innerWidth * 0.3;
      setSidebarWidth(thirtyPercentWidth);
      localStorage.setItem("sidebarWidth", String(thirtyPercentWidth));
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (!isCollapsed && !localStorage.getItem("sidebarCustomWidth")) {
        const thirtyPercent = window.innerWidth * 0.3;
        setSidebarWidth(thirtyPercent);
        localStorage.setItem("sidebarWidth", String(thirtyPercent));
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isCollapsed]);

  useEffect(() => {
    const saved = localStorage.getItem("sidebarCategories");
    if (saved) setOpenCategories(JSON.parse(saved));
  }, []);

  useEffect(() => {
    if (activeRowRef.current && scrollContainerRef.current && !isCollapsed) {
      activeRowRef.current.scrollIntoView({
        block: "center",
        behavior: "smooth",
      });
    }
  }, [activeInstrument?.id, isCollapsed]);

  useEffect(() => {
    const flash = {};
    INSTRUMENTS.forEach((inst) => {
      const currentPrice = marketData[inst.id]?.price;
      const prevPrice = prevPricesRef.current[inst.id];
      if (
        prevPrice !== undefined &&
        currentPrice !== undefined &&
        prevPrice !== currentPrice
      ) {
        flash[inst.id] = currentPrice > prevPrice ? "up" : "dn";
      }
      if (currentPrice !== undefined)
        prevPricesRef.current[inst.id] = currentPrice;
    });
    if (Object.keys(flash).length > 0) {
      setPriceFlash(flash);
      setTimeout(() => setPriceFlash({}), 900);
    }
  }, [marketData]);

  useEffect(() => {
    const fetchCreditAndBonus = async () => {
      try {
        const [financeRes, creditRes] = await Promise.all([
          api.get("/user/finance/summary"),
          api.get("/user/credit/requests"),
        ]);
        setBonusBalance(financeRes.data?.bonusBalance || 0);
        if (creditRes.data && Array.isArray(creditRes.data)) {
          const approved = creditRes.data
            .filter((r) => r.status === "approved")
            .reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
          setTotalCredit(approved);
        }
      } catch (e) {}
    };
    fetchCreditAndBonus();
  }, []);

  useEffect(() => {
    setManualLotInput(String(lotSize));
  }, [lotSize]);

  const handleLotChange = (delta) => {
    let newLot = parseFloat(manualLotInput) + delta;
    if (isNaN(newLot)) newLot = 0.01;
    if (newLot < 0.01) newLot = 0.01;
    if (newLot > 100) newLot = 100;
    newLot = parseFloat(newLot.toFixed(2));
    dispatch(setLotSize(newLot));
    setManualLotInput(String(newLot));
  };

  const handleLotInput = (e) => {
    const raw = e.target.value;
    setManualLotInput(raw);
    let val = parseFloat(raw);
    if (isNaN(val) || val < 0.01) return;
    if (val > 100) val = 100;
    dispatch(setLotSize(parseFloat(val.toFixed(2))));
  };

  const handleLotBlur = () => {
    let val = parseFloat(manualLotInput);
    if (isNaN(val) || val < 0.01) val = 0.01;
    if (val > 100) val = 100;
    val = parseFloat(val.toFixed(2));
    dispatch(setLotSize(val));
    setManualLotInput(String(val));
  };

  const toggleCategory = (cat) => {
    const newState = { ...openCategories, [cat]: !openCategories[cat] };
    setOpenCategories(newState);
    localStorage.setItem("sidebarCategories", JSON.stringify(newState));
  };

  const filteredInstruments = INSTRUMENTS.filter((inst) => {
    if (sidebarTab !== "all" && inst.cat !== sidebarTab) return false;
    if (sidebarCat === "fav" && !favorites.includes(inst.id)) return false;
    if (
      searchQuery &&
      !inst.id.toLowerCase().includes(searchQuery.toLowerCase()) &&
      !inst.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
      return false;
    return true;
  });

  const groupedInstruments = {};
  CAT_ORDER.forEach((cat) => {
    const items = filteredInstruments.filter((inst) => inst.cat === cat);
    if (items.length > 0) groupedInstruments[cat] = items;
  });

  const handleSelectInstrument = (inst) => {
    dispatch(setActiveInstrument(inst));
    setExpandedInstrument((prev) => (prev === inst.id ? null : inst.id));
  };

  const handleToggleFavorite = (e, instId) => {
    e.stopPropagation();
    dispatch(toggleFavorite(instId));
  };

  const handleSidebarDragStart = useCallback(
    (e) => {
      e.preventDefault();
      isDraggingSidebar.current = true;
      dragStartX.current = e.clientX;
      dragStartWidth.current = isCollapsed ? 0 : sidebarWidth;
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    },
    [sidebarWidth, isCollapsed],
  );

  useEffect(() => {
    const handleSidebarDragMove = (e) => {
      if (!isDraggingSidebar.current) return;
      const delta = e.clientX - dragStartX.current;
      let newWidth = dragStartWidth.current + delta;

      if (isCollapsed) {
        newWidth = delta;
      }

      if (newWidth <= COLLAPSE_WIDTH) {
        setIsCollapsed(true);
        localStorage.setItem("sidebarCollapsed", "true");
      } else if (newWidth >= MIN_WIDTH) {
        setIsCollapsed(false);
        setSidebarWidth(newWidth);
        localStorage.setItem("sidebarCollapsed", "false");
        localStorage.setItem("sidebarWidth", String(newWidth));
        localStorage.setItem("sidebarCustomWidth", "true");
      }
    };
    const handleSidebarDragEnd = () => {
      if (!isDraggingSidebar.current) return;
      isDraggingSidebar.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      if (!isCollapsed) {
        localStorage.setItem("sidebarWidth", String(sidebarWidth));
        localStorage.setItem("sidebarCustomWidth", "true");
      }
    };
    window.addEventListener("mousemove", handleSidebarDragMove);
    window.addEventListener("mouseup", handleSidebarDragEnd);
    return () => {
      window.removeEventListener("mousemove", handleSidebarDragMove);
      window.removeEventListener("mouseup", handleSidebarDragEnd);
    };
  }, [sidebarWidth, isCollapsed]);

  const handleSplitDragStart = useCallback(
    (e) => {
      e.preventDefault();
      isDraggingSplit.current = true;
      dragStartY.current = e.clientY;
      dragStartRatio.current = splitRatio;
      document.body.style.cursor = "row-resize";
      document.body.style.userSelect = "none";
    },
    [splitRatio],
  );

  useEffect(() => {
    const handleSplitDragMove = (e) => {
      if (!isDraggingSplit.current || !sidebarRef.current) return;
      const rect = sidebarRef.current.getBoundingClientRect();
      const totalHeight = rect.height;
      const delta = e.clientY - dragStartY.current;
      const newRatio = dragStartRatio.current + delta / totalHeight;
      if (newRatio >= 0.5 && newRatio <= 0.9) setSplitRatio(newRatio);
    };
    const handleSplitDragEnd = () => {
      if (!isDraggingSplit.current) return;
      isDraggingSplit.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      localStorage.setItem("sidebarSplitRatio", String(splitRatio));
    };
    window.addEventListener("mousemove", handleSplitDragMove);
    window.addEventListener("mouseup", handleSplitDragEnd);
    return () => {
      window.removeEventListener("mousemove", handleSplitDragMove);
      window.removeEventListener("mouseup", handleSplitDragEnd);
    };
  }, [splitRatio]);

  const activeInst = activeInstrument;
  const activeData = activeInst ? marketData[activeInst.id] : null;

  const toggleCollapse = () => {
    if (isCollapsed) {
      setIsCollapsed(false);
      localStorage.setItem("sidebarCollapsed", "false");
      const savedWidth = localStorage.getItem("sidebarWidth");
      setSidebarWidth(
        savedWidth ? parseFloat(savedWidth) : window.innerWidth * 0.3,
      );
    } else {
      lastWidthRef.current = sidebarWidth;
      setIsCollapsed(true);
      localStorage.setItem("sidebarCollapsed", "true");
    }
  };

  if (isCollapsed) {
    return (
      <div
        className="relative flex-shrink-0 flex flex-col items-center overflow-hidden"
        style={{
          width: "24px",
          background: "var(--color-bgSurface)",
          borderRight: "1px solid var(--color-border)",
        }}
      >
        <button
          onClick={toggleCollapse}
          className="w-full py-3 flex items-center justify-center cursor-pointer transition-colors hover:bg-[var(--color-bgHover)]"
          title="Expand sidebar"
        >
          <i
            className="fas fa-chevron-right text-[10px]"
            style={{ color: "var(--color-text)" }}
          />
        </button>

        <div
          onMouseDown={handleSidebarDragStart}
          className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-[var(--color-orange)] transition-colors group"
          style={{ zIndex: 50 }}
        >
          <div
            className="absolute top-1/2 -translate-y-1/2 -right-2 w-4 h-8 rounded-full flex items-center justify-center cursor-col-resize transition-all group-hover:scale-110"
            style={{
              background: "var(--color-orangeBg)",
              border: "1px solid var(--color-orangeBorder)",
            }}
          >
            <i
              className="fas fa-grip-lines-vertical text-[8px]"
              style={{ color: "var(--color-text)" }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={sidebarRef}
      className="relative flex-shrink-0 flex flex-col overflow-hidden"
      style={{
        width: `${sidebarWidth}px`,
        background: "var(--color-bgSurface)",
        borderRight: "1px solid var(--color-border)",
      }}
    >
      <button
        onClick={toggleCollapse}
        className="absolute top-1/2 -translate-y-1/2 left-1 w-5 h-8 rounded-full flex items-center justify-center cursor-pointer transition-all hover:scale-110 z-[60]"
        style={{
          background: "var(--color-orangeBg)",
          border: "1px solid var(--color-orangeBorder)",
        }}
        title="Collapse sidebar"
      >
        <i
          className="fas fa-chevron-left text-[8px]"
          style={{ color: "var(--color-text)" }}
        />
      </button>

      <div
        className="flex flex-col overflow-hidden"
        style={{ height: `${splitRatio * 100}%` }}
      >
        <div className="flex p-2 gap-1.5 flex-shrink-0 pl-8">
          <div className="relative flex-1">
            <select
              value={sidebarTab}
              onChange={(e) => dispatch(setSidebarTab(e.target.value))}
              className="w-full py-2 pl-3 pr-7 text-[11px] font-syne font-bold rounded-lg outline-none cursor-pointer appearance-none transition-colors"
              style={{
                background: "var(--color-bgSurfaceLight)",
                border: "1px solid var(--color-borderStrong)",
                color: "var(--color-text)",
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%239ca3af' d='M5 6L0 0h10z'/%3E%3C/svg%3E")`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 10px center",
              }}
              onFocus={(e) =>
                (e.target.style.borderColor = "var(--color-orange)")
              }
              onBlur={(e) =>
                (e.target.style.borderColor = "var(--color-borderStrong)")
              }
            >
              <option
                value="all"
                style={{
                  background: "var(--color-bgElevated)",
                  color: "var(--color-text)",
                }}
              >
                All Categories
              </option>
              {CAT_ORDER.map((cat) => (
                <option
                  key={cat}
                  value={cat}
                  style={{
                    background: "var(--color-bgElevated)",
                    color: "var(--color-text)",
                  }}
                >
                  {CAT_LABELS[cat]}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={() =>
              dispatch(setSidebarCat(sidebarCat === "fav" ? "all" : "fav"))
            }
            className={`flex items-center justify-center px-3 py-2 rounded-lg text-[11px] font-syne font-bold transition-all border ${sidebarCat === "fav" ? "shadow-[0_0_12px_rgba(249,115,22,0.15)]" : ""}`}
            style={{
              background:
                sidebarCat === "fav"
                  ? "var(--color-orangeBg)"
                  : "var(--color-bgSurfaceLight)",
              borderColor:
                sidebarCat === "fav"
                  ? "var(--color-orangeBorder)"
                  : "var(--color-borderStrong)",
              color:
                sidebarCat === "fav" ? "#f59e0b" : "var(--color-textSecondary)",
            }}
            title="Show Favorites"
          >
            <i
              className={`fas fa-star text-xs ${sidebarCat === "fav" ? "text-[#f59e0b]" : ""}`}
            />
          </button>
        </div>

        <div className="relative mx-2 my-2 flex-shrink-0">
          <i
            className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-[10px]"
            style={{ color: "var(--color-textSecondary)" }}
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Filter symbols..."
            className="w-full rounded-lg py-2 pl-8 pr-3 font-dm text-[11px] font-bold outline-none transition-all"
            style={{
              background: "var(--color-bgInput)",
              border: "1px solid var(--color-borderMedium)",
              color: "var(--color-text)",
            }}
            onFocus={(e) =>
              (e.target.style.borderColor = "var(--color-orange)")
            }
            onBlur={(e) =>
              (e.target.style.borderColor = "var(--color-borderMedium)")
            }
          />
        </div>

        <div
          className="grid grid-cols-[1fr_62px] px-3 py-1.5 text-[9px] font-mono font-extrabold uppercase tracking-wider flex-shrink-0"
          style={{
            color: "var(--color-textSecondary)",
            borderBottom: "1px solid var(--color-borderLight)",
          }}
        >
          <span>Symbol</span>
          <span className="text-right">Bid / Δ</span>
        </div>

        <div
          ref={scrollContainerRef}
          className="flex-1 overflow-y-auto sidebar-scroll"
        >
          {Object.keys(groupedInstruments).length === 0 ? (
            <div className="py-10 text-center">
              <i
                className="fas fa-search text-xl mb-3 block opacity-40"
                style={{ color: "var(--color-textDim)" }}
              />
              <p
                className="text-xs font-mono font-bold"
                style={{ color: "var(--color-textSecondary)" }}
              >
                No instruments found
              </p>
            </div>
          ) : (
            Object.entries(groupedInstruments).map(([cat, instruments]) => (
              <div key={cat}>
                <div
                  onClick={() => toggleCategory(cat)}
                  className="flex items-center justify-between px-3 py-2 cursor-pointer transition-colors"
                  style={{
                    background: "var(--color-bgCard)",
                    borderBottom: "1px solid var(--color-borderLight)",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "var(--color-bgHover)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "var(--color-bgCard)")
                  }
                >
                  <div className="flex items-center gap-2">
                    <i
                      className={`fas ${CAT_ICONS[cat]} text-[11px]`}
                      style={{ color: "var(--color-text)" }}
                    />
                    <span
                      className="text-[10px] font-extrabold tracking-[0.1em] font-mono uppercase"
                      style={{ color: "var(--color-textSecondary)" }}
                    >
                      {cat}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className="text-[10px] font-mono font-bold"
                      style={{ color: "var(--color-textSecondary)" }}
                    >
                      {instruments.length}
                    </span>
                    <i
                      className={`fas fa-chevron-down text-[9px] transition-transform duration-200 ${openCategories[cat] ? "rotate-180" : ""}`}
                      style={{ color: "var(--color-textSecondary)" }}
                    />
                  </div>
                </div>

                {openCategories[cat] && (
                  <div>
                    {instruments.map((inst) => {
                      const data = marketData[inst.id];
                      const price = data?.price || 0;
                      const pct = data?.changePct || 0;
                      const direction =
                        pct > 0.01 ? "up" : pct < -0.01 ? "dn" : "neu";
                      const isActive = activeInstrument?.id === inst.id;
                      const isExpanded = expandedInstrument === inst.id;
                      const marketStatus = getMarketStatus(inst.id, inst.cat);
                      const hasFlash = priceFlash[inst.id];

                      return (
                        <div key={inst.id}>
                          <div
                            ref={isActive ? activeRowRef : null}
                            onClick={() => handleSelectInstrument(inst)}
                            className={`grid grid-cols-[1fr_62px] px-3 py-2 cursor-pointer transition-all duration-150 relative ${isActive ? "border-l-[3px]" : "border-l-[3px] border-l-transparent"}`}
                            style={{
                              background: isActive
                                ? "var(--color-orangeBg)"
                                : "transparent",
                              borderBottom: isExpanded
                                ? "none"
                                : "1px solid var(--color-borderLight)",
                              borderLeftColor: isActive
                                ? "var(--color-orange)"
                                : "transparent",
                            }}
                            onMouseEnter={(e) => {
                              if (!isActive)
                                e.currentTarget.style.background =
                                  "var(--color-bgHover)";
                            }}
                            onMouseLeave={(e) => {
                              if (!isActive)
                                e.currentTarget.style.background =
                                  "transparent";
                            }}
                          >
                            <div className="flex items-center gap-1.5 relative z-10">
                              <button
                                onClick={(e) =>
                                  handleToggleFavorite(e, inst.id)
                                }
                                className="text-[10px] transition-colors"
                                style={{
                                  color: favorites.includes(inst.id)
                                    ? "#f59e0b"
                                    : "var(--color-textDim)",
                                }}
                              >
                                <i className="fas fa-star" />
                              </button>
                              
                              {/* Flag/Icon Display */}
                              <InstrumentIcon inst={inst} />
                              
                              <span
                                className="text-xs font-syne font-extrabold"
                                style={{ color: "var(--color-text)" }}
                              >
                                {getDisplayId(inst)}
                              </span>
                              {!marketStatus.open && (
                                <span
                                  className="w-2 h-2 rounded-full bg-[#ef4444]"
                                  title="Market Closed"
                                />
                              )}
                            </div>
                            <div className="text-right relative z-10">
                              <div
                                className={`font-mono text-[11px] font-extrabold ${hasFlash === "up" ? "price-flash-up" : hasFlash === "dn" ? "price-flash-dn" : ""}`}
                                style={{ color: "var(--color-text)" }}
                              >
                                {fmtP(price, inst.cat)}
                              </div>
                              <div
                                className="text-[10px] font-mono font-bold"
                                style={{
                                  color:
                                    direction === "up"
                                      ? "var(--color-green)"
                                      : direction === "dn"
                                        ? "var(--color-red)"
                                        : "var(--color-textSecondary)",
                                }}
                              >
                                {pct > 0 ? "+" : ""}
                                {pct.toFixed(2)}%
                              </div>
                            </div>
                          </div>

                          {/* Slide-down Buy/Sell Panel with Lot Size */}
                          {isExpanded && (
                            <div
                              className="animate-slide-down"
                              style={{
                                background: "var(--color-bgCard)",
                                borderBottom:
                                  "1px solid var(--color-borderLight)",
                                borderLeft: "3px solid var(--color-orange)",
                                overflow: "hidden",
                              }}
                            >
                              <div className="p-3">
                                <div className="text-center mb-2.5">
                                  <span
                                    className="text-[11px] font-mono font-extrabold"
                                    style={{ color: "var(--color-text)" }}
                                  >
                                    {inst.id} ·{" "}
                                    <span
                                      style={{
                                        color:
                                          (data?.changePct || 0) >= 0
                                            ? "var(--color-green)"
                                            : "var(--color-red)",
                                      }}
                                    >
                                      {fmtP(price, inst.cat)}
                                    </span>
                                  </span>
                                </div>

                                <div className="mb-2.5">
                                  <div className="flex items-center justify-between mb-1">
                                    <span
                                      className="text-[8px] font-mono font-bold uppercase"
                                      style={{
                                        color: "var(--color-textSecondary)",
                                      }}
                                    >
                                      Lot Size
                                    </span>
                                    <span
                                      className="text-[8px] font-mono"
                                      style={{
                                        color: "var(--color-textSecondary)",
                                      }}
                                    >
                                      {LOT_LABELS[inst.cat]}
                                    </span>
                                  </div>
                                  <div
                                    className="flex items-center rounded-lg overflow-hidden"
                                    style={{
                                      border:
                                        "1px solid var(--color-borderStrong)",
                                    }}
                                  >
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleLotChange(-0.01);
                                      }}
                                      className="w-8 h-8 cursor-pointer text-base font-bold flex items-center justify-center active:scale-90 transition-all"
                                      style={{
                                        background:
                                          "var(--color-bgSurfaceLight)",
                                        border: "none",
                                        color: "var(--color-text)",
                                      }}
                                    >
                                      −
                                    </button>
                                    <input
                                      type="text"
                                      inputMode="decimal"
                                      value={manualLotInput}
                                      onChange={handleLotInput}
                                      onBlur={handleLotBlur}
                                      onClick={(e) => e.stopPropagation()}
                                      className="flex-1 text-center font-mono text-sm font-bold py-1.5 outline-none border-x"
                                      style={{
                                        background: "var(--color-bgInput)",
                                        borderColor:
                                          "var(--color-borderStrong)",
                                        color: "var(--color-text)",
                                      }}
                                    />
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleLotChange(0.01);
                                      }}
                                      className="w-8 h-8 cursor-pointer text-base font-bold flex items-center justify-center active:scale-90 transition-all"
                                      style={{
                                        background:
                                          "var(--color-bgSurfaceLight)",
                                        border: "none",
                                        color: "var(--color-text)",
                                      }}
                                    >
                                      +
                                    </button>
                                  </div>
                                </div>

                                <div className="grid grid-cols-2 gap-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (marketStatus.open) {
                                        dispatch(setActiveInstrument(inst));
                                        dispatch(openModal("buyModal"));
                                      }
                                    }}
                                    disabled={!marketStatus.open}
                                    className="py-2.5 rounded-lg font-syne font-extrabold text-[12px] tracking-wide transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
                                    style={{
                                      background: "var(--color-green)",
                                      border: "none",
                                      color: "#ffffff",
                                      boxShadow:
                                        "0 2px 8px rgba(16,185,129,0.4)",
                                    }}
                                  >
                                    <i className="fas fa-arrow-trend-up mr-1 text-[10px]" />{" "}
                                    BUY
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (marketStatus.open) {
                                        dispatch(setActiveInstrument(inst));
                                        dispatch(openModal("sellModal"));
                                      }
                                    }}
                                    disabled={!marketStatus.open}
                                    className="py-2.5 rounded-lg font-syne font-extrabold text-[12px] tracking-wide transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
                                    style={{
                                      background: "var(--color-red)",
                                      border: "none",
                                      color: "#ffffff",
                                      boxShadow:
                                        "0 2px 8px rgba(239,68,68,0.4)",
                                    }}
                                  >
                                    <i className="fas fa-arrow-trend-down mr-1 text-[10px]" />{" "}
                                    SELL
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      <div
        onMouseDown={handleSplitDragStart}
        className="relative flex-shrink-0 cursor-row-resize group"
        style={{
          height: "6px",
          background: "var(--color-borderMedium)",
          zIndex: 40,
        }}
      >
        <div
          className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 w-8 h-4 rounded-full flex items-center justify-center transition-all group-hover:scale-110"
          style={{
            background: "var(--color-orangeBg)",
            border: "1px solid var(--color-orangeBorder)",
          }}
        >
          <i
            className="fas fa-grip-lines text-[7px]"
            style={{ color: "var(--color-text)" }}
          />
        </div>
      </div>

      <div
        className="flex-1 flex flex-col overflow-hidden"
        style={{ background: "var(--color-bgSurface)" }}
      >
        <div
          className="flex flex-shrink-0"
          style={{ borderBottom: "1px solid var(--color-borderMedium)" }}
        >
          {[
            { key: "info", label: "Info", icon: "fa-circle-info" },
            { key: "performance", label: "Performance", icon: "fa-chart-line" },
            { key: "credit", label: "Credit", icon: "fa-credit-card" },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setBottomTab(tab.key)}
              className="flex-1 py-2.5 text-[10px] font-syne font-bold flex items-center justify-center gap-1.5 transition-all border-b-2"
              style={{
                color:
                  bottomTab === tab.key
                    ? "var(--color-text)"
                    : "var(--color-textSecondary)",
                borderColor:
                  bottomTab === tab.key ? "var(--color-orange)" : "transparent",
                background:
                  bottomTab === tab.key
                    ? "var(--color-orangeBg)"
                    : "transparent",
              }}
            >
              <i className={`fas ${tab.icon} text-[9px]`} /> {tab.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto sidebar-scroll p-2">
          {bottomTab === "info" &&
            (activeInst ? (
              <div>
                <div
                  className="text-center mb-3 p-3 rounded-xl"
                  style={{
                    background:
                      "linear-gradient(135deg, var(--color-orangeBg), var(--color-purpleBg))",
                    border: "1px solid var(--color-orangeBorder)",
                  }}
                >
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <InstrumentIcon inst={activeInst} />
                    <span
                      className="font-syne text-base font-extrabold"
                      style={{ color: "var(--color-text)" }}
                    >
                      {activeInst.id}
                    </span>
                  </div>
                  <div
                    className="text-[10px] font-mono mt-1"
                    style={{ color: "var(--color-textSecondary)" }}
                  >
                    {activeInst.name}
                  </div>
                  <div
                    className="text-[12px] font-mono font-extrabold mt-1.5"
                    style={{ color: "var(--color-text)" }}
                  >
                    {fmtP(activeData?.price || 0, activeInst.cat)}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  {[
                    {
                      label: "Category",
                      value: activeInst.cat?.toUpperCase(),
                      icon: "fa-tag",
                      color: "var(--color-purpleLight)",
                    },
                    {
                      label: "High",
                      value: fmtP(activeData?.high || 0, activeInst.cat),
                      icon: "fa-arrow-up",
                      color: "var(--color-green)",
                    },
                    {
                      label: "Low",
                      value: fmtP(activeData?.low || 0, activeInst.cat),
                      icon: "fa-arrow-down",
                      color: "var(--color-red)",
                    },
                    {
                      label: "Open",
                      value: fmtP(activeData?.open || 0, activeInst.cat),
                      icon: "fa-door-open",
                      color: "var(--color-text)",
                    },
                    {
                      label: "Change %",
                      value: `${(activeData?.changePct || 0) > 0 ? "+" : ""}${(activeData?.changePct || 0).toFixed(2)}%`,
                      icon: "fa-percent",
                      color:
                        (activeData?.changePct || 0) >= 0
                          ? "var(--color-green)"
                          : "var(--color-red)",
                    },
                    {
                      label: "Lot Size",
                      value: getLotSize(
                        activeInst.cat,
                        activeInst.id,
                      ).toLocaleString(),
                      icon: "fa-layer-group",
                      color: "var(--color-blue)",
                    },
                    {
                      label: "Point Size",
                      value: activeInst.precision
                        ? `0.${"0".repeat(activeInst.precision - 1)}1`
                        : "0.01",
                      icon: "fa-crosshairs",
                      color: "var(--color-yellow)",
                    },
                    {
                      label: "Spread",
                      value:
                        activeInst.cat === "forex"
                          ? "0.0002"
                          : `${((activeData?.price || 0) * 0.002).toFixed(2)}`,
                      icon: "fa-arrows-left-right",
                      color: "var(--color-yellow)",
                    },
                    {
                      label: "Volume",
                      value: activeData?.volume
                        ? Number(activeData.volume).toLocaleString()
                        : "—",
                      icon: "fa-chart-bar",
                      color: "var(--color-cyan)",
                    },
                    {
                      label: "Source",
                      value: activeData?.source || "—",
                      icon: "fa-database",
                      color: "var(--color-textSecondary)",
                    },
                  ].map((box, i) => (
                    <div
                      key={i}
                      className="rounded-xl p-2.5 transition-all hover:scale-[1.03]"
                      style={{
                        background: "var(--color-bgCard)",
                        border: "1px solid var(--color-borderMedium)",
                      }}
                    >
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <i
                          className={`fas ${box.icon} text-[9px]`}
                          style={{ color: box.color }}
                        />
                        <span
                          className="text-[8px] font-mono font-bold uppercase tracking-wider"
                          style={{ color: "var(--color-textSecondary)" }}
                        >
                          {box.label}
                        </span>
                      </div>
                      <div
                        className="text-[11px] font-mono font-extrabold"
                        style={{ color: box.color }}
                      >
                        {box.value}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full py-12 text-center">
                <i
                  className="fas fa-circle-info text-2xl mb-3 opacity-40"
                  style={{ color: "var(--color-textDim)" }}
                />
                <p
                  className="text-[11px] font-mono"
                  style={{ color: "var(--color-textSecondary)" }}
                >
                  Select an instrument
                </p>
              </div>
            ))}

          {bottomTab === "performance" &&
            (activeInst ? (
              <div>
                <div
                  className="text-center mb-3 p-3 rounded-xl"
                  style={{
                    background:
                      "linear-gradient(135deg, var(--color-orangeBg), var(--color-purpleBg))",
                    border: "1px solid var(--color-orangeBorder)",
                  }}
                >
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <InstrumentIcon inst={activeInst} />
                    <span
                      className="font-syne text-base font-extrabold"
                      style={{ color: "var(--color-text)" }}
                    >
                      {activeInst.id}
                    </span>
                  </div>
                  <div
                    className="text-[10px] font-mono mt-1"
                    style={{ color: "var(--color-textSecondary)" }}
                  >
                    Price Performance
                  </div>
                </div>

                {[
                  {
                    label: "Day",
                    period: "24h",
                    open: activeData?.open || activeData?.price || 0,
                    current: activeData?.price || 0,
                    high: activeData?.high || activeData?.price || 0,
                    low: activeData?.low || activeData?.price || 0,
                  },
                  {
                    label: "Week",
                    period: "7d",
                    open: (activeData?.open || activeData?.price || 0) * 0.995,
                    current: activeData?.price || 0,
                    high: (activeData?.high || activeData?.price || 0) * 1.01,
                    low: (activeData?.low || activeData?.price || 0) * 0.99,
                  },
                  {
                    label: "Month",
                    period: "30d",
                    open: (activeData?.open || activeData?.price || 0) * 0.98,
                    current: activeData?.price || 0,
                    high: (activeData?.high || activeData?.price || 0) * 1.05,
                    low: (activeData?.low || activeData?.price || 0) * 0.95,
                  },
                ].map((perf, i) => {
                  const openPrice = perf.open;
                  const currentPrice = perf.current;
                  const changePct =
                    openPrice > 0
                      ? ((currentPrice - openPrice) / openPrice) * 100
                      : 0;
                  const isPositive = changePct >= 0;
                  const range = perf.high - perf.low;
                  const openPos =
                    range > 0 ? ((openPrice - perf.low) / range) * 100 : 50;
                  const currentPos =
                    range > 0 ? ((currentPrice - perf.low) / range) * 100 : 50;
                  const lineStart = Math.min(openPos, currentPos);
                  const lineEnd = Math.max(openPos, currentPos);
                  const lineWidth = lineEnd - lineStart;

                  return (
                    <div
                      key={i}
                      className="rounded-xl p-3 mb-2"
                      style={{
                        background: "var(--color-bgCard)",
                        border: "1px solid var(--color-borderMedium)",
                      }}
                    >
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center gap-2">
                          <span
                            className="text-[10px] font-syne font-extrabold"
                            style={{ color: "var(--color-text)" }}
                          >
                            {perf.label}
                          </span>
                          <span
                            className="text-[8px] font-mono px-1.5 py-0.5 rounded"
                            style={{
                              background: "var(--color-bgSurfaceLight)",
                              color: "var(--color-textSecondary)",
                            }}
                          >
                            {perf.period}
                          </span>
                        </div>
                        <span
                          className="text-[11px] font-mono font-extrabold px-2 py-0.5 rounded-lg border"
                          style={{
                            background: isPositive
                              ? "var(--color-greenBg)"
                              : "var(--color-redBg)",
                            color: isPositive
                              ? "var(--color-green)"
                              : "var(--color-red)",
                            borderColor: isPositive
                              ? "var(--color-greenBorder)"
                              : "var(--color-redBorder)",
                          }}
                        >
                          {isPositive ? "+" : ""}
                          {changePct.toFixed(2)}%
                        </span>
                      </div>

                      <div
                        className="relative h-3 rounded-full overflow-hidden mb-2"
                        style={{ background: "rgba(239,68,68,0.25)" }}
                      >
                        {isPositive && (
                          <div
                            className="absolute inset-y-0 rounded-full"
                            style={{
                              left: `${lineStart}%`,
                              width: `${lineWidth}%`,
                              background:
                                "linear-gradient(90deg, var(--color-greenDark), var(--color-green))",
                              boxShadow: "0 0 6px var(--color-green)",
                            }}
                          />
                        )}
                        {!isPositive && (
                          <div
                            className="absolute inset-y-0 rounded-full"
                            style={{
                              left: `${lineStart}%`,
                              width: `${lineWidth}%`,
                              background:
                                "linear-gradient(90deg, var(--color-redDark), var(--color-red))",
                              boxShadow: "0 0 6px var(--color-red)",
                            }}
                          />
                        )}
                        <div
                          className="absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-white border border-[var(--color-bg)]"
                          style={{ left: `calc(${openPos}% - 4px)` }}
                        />
                        <div
                          className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full border-2"
                          style={{
                            left: `calc(${currentPos}% - 6px)`,
                            background: isPositive
                              ? "var(--color-green)"
                              : "var(--color-red)",
                            borderColor: "var(--color-text)",
                            boxShadow: `0 0 8px ${isPositive ? "var(--color-green)" : "var(--color-red)"}`,
                          }}
                        />
                      </div>

                      <div className="flex justify-between text-[8px] font-mono font-bold">
                        <span style={{ color: "var(--color-red)" }}>
                          Low: {fmtP(perf.low, activeInst.cat)}
                        </span>
                        <span style={{ color: "var(--color-textSecondary)" }}>
                          Open: {fmtP(openPrice, activeInst.cat)} → Now:{" "}
                          {fmtP(currentPrice, activeInst.cat)}
                        </span>
                        <span style={{ color: "var(--color-green)" }}>
                          High: {fmtP(perf.high, activeInst.cat)}
                        </span>
                      </div>
                    </div>
                  );
                })}

                <div
                  className="mt-3 p-3 rounded-xl"
                  style={{
                    background: "var(--color-bgCard)",
                    border: "1px solid var(--color-borderMedium)",
                  }}
                >
                  <div
                    className="text-[9px] font-mono font-bold uppercase mb-2"
                    style={{ color: "var(--color-textSecondary)" }}
                  >
                    Summary
                  </div>
                  <div className="flex justify-between items-center">
                    <span
                      className="text-[10px] font-mono"
                      style={{ color: "var(--color-textSecondary)" }}
                    >
                      Day Change
                    </span>
                    <span
                      className="text-[11px] font-mono font-extrabold"
                      style={{
                        color:
                          (activeData?.changePct || 0) >= 0
                            ? "var(--color-green)"
                            : "var(--color-red)",
                      }}
                    >
                      {(activeData?.changePct || 0) >= 0 ? "+" : ""}
                      {(activeData?.changePct || 0).toFixed(2)}%
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full py-12 text-center">
                <i
                  className="fas fa-chart-line text-2xl mb-3 opacity-40"
                  style={{ color: "var(--color-textDim)" }}
                />
                <p
                  className="text-[11px] font-mono"
                  style={{ color: "var(--color-textSecondary)" }}
                >
                  Select an instrument
                </p>
              </div>
            ))}

          {bottomTab === "credit" && (
            <div>
              <div
                className="text-center mb-3 p-3 rounded-xl"
                style={{
                  background:
                    "linear-gradient(135deg, var(--color-orangeBg), var(--color-purpleBg))",
                  border: "1px solid var(--color-orangeBorder)",
                }}
              >
                <div
                  className="font-syne text-base font-extrabold"
                  style={{ color: "var(--color-text)" }}
                >
                  Credit
                </div>
                <div
                  className="text-[10px] font-mono mt-1"
                  style={{ color: "var(--color-textSecondary)" }}
                >
                  Trading Credit Info
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  {
                    label: "Balance",
                    value: fmtUSD(balance),
                    icon: "fa-wallet",
                    color: "var(--color-green)",
                  },
                  {
                    label: "Credit Limit",
                    value: fmtUSD(Number(user?.creditLimit) || 0),
                    icon: "fa-credit-card",
                    color: "var(--color-blue)",
                  },
                  {
                    label: "Credit Given",
                    value: fmtUSD(totalCredit),
                    icon: "fa-hand-holding-dollar",
                    color: "var(--color-blue)",
                  },
                  {
                    label: "Bonus",
                    value: fmtUSD(bonusBalance),
                    icon: "fa-gift",
                    color: "var(--color-purpleLight)",
                  },
                ].map((box, i) => (
                  <div
                    key={i}
                    className="rounded-xl p-3 transition-all hover:scale-[1.03]"
                    style={{
                      background: "var(--color-bgCard)",
                      border: "1px solid var(--color-borderMedium)",
                    }}
                  >
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <i
                        className={`fas ${box.icon} text-[9px]`}
                        style={{ color: box.color }}
                      />
                      <span
                        className="text-[8px] font-mono font-bold uppercase tracking-wider"
                        style={{ color: "var(--color-textSecondary)" }}
                      >
                        {box.label}
                      </span>
                    </div>
                    <div
                      className="text-[11px] font-mono font-extrabold"
                      style={{ color: box.color }}
                    >
                      {box.value}
                    </div>
                  </div>
                ))}
              </div>
              <button
                onClick={() => (window.location.href = "/credit")}
                className="w-full mt-3 py-3 rounded-xl font-syne font-bold text-[11px] transition-all active:scale-95"
                style={{
                  background: "var(--color-orangeBg)",
                  border: "1px solid var(--color-orangeBorder)",
                  color: "var(--color-text)",
                }}
              >
                <i className="fas fa-credit-card mr-1.5" /> Request Credit
              </button>
            </div>
          )}
        </div>
      </div>

      <div
        onMouseDown={handleSidebarDragStart}
        className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-[var(--color-orange)] transition-colors group"
        style={{ zIndex: 50 }}
      >
        <div
          className="absolute top-1/2 -translate-y-1/2 -right-2 w-4 h-8 rounded-full flex items-center justify-center cursor-col-resize transition-all group-hover:scale-110"
          style={{
            background: "var(--color-orangeBg)",
            border: "1px solid var(--color-orangeBorder)",
            boxShadow: "0 0 8px var(--color-orangeGlow)",
          }}
        >
          <i
            className="fas fa-grip-lines-vertical text-[8px]"
            style={{ color: "var(--color-text)" }}
          />
        </div>
      </div>

      <style>{`
        .sidebar-scroll::-webkit-scrollbar { width: 6px; }
        .sidebar-scroll::-webkit-scrollbar-track { background: transparent; }
        .sidebar-scroll::-webkit-scrollbar-thumb { background: var(--color-scrollbarThumb); border-radius: 10px; }
        .sidebar-scroll::-webkit-scrollbar-thumb:hover { background: var(--color-scrollbarThumbHover); }
        .sidebar-scroll { scrollbar-width: auto; scrollbar-color: var(--color-scrollbarThumb) transparent; }
        .price-flash-up { animation: flashUp 0.7s ease-out; color: #34d399 !important; }
        .price-flash-dn { animation: flashDn 0.7s ease-out; color: #f87171 !important; }
        @keyframes flashUp {
          0% { background: rgba(16, 185, 129, 0.5); transform: scale(1.12); border-radius: 3px; padding: 2px 6px; }
          50% { background: rgba(16, 185, 129, 0.2); transform: scale(1.04); }
          100% { background: transparent; transform: scale(1); }
        }
        @keyframes flashDn {
          0% { background: rgba(239, 68, 68, 0.5); transform: scale(1.12); border-radius: 3px; padding: 2px 6px; }
          50% { background: rgba(239, 68, 68, 0.2); transform: scale(0.96); }
          100% { background: transparent; transform: scale(1); }
        }
        @keyframes slide-down {
          from { opacity: 0; transform: translateY(-8px); max-height: 0; }
          to { opacity: 1; transform: translateY(0); max-height: 120px; }
        }
        .animate-slide-down { animation: slide-down 0.25s ease-out; }
      `}</style>
    </div>
  );
};

export default Sidebar;